// ConsiderSupport.jsx (camelCase-ready)
import React, { useEffect, useMemo, useState } from "react";
import {
    Container, Row, Col,
    Card, CardBody, CardHeader, FormGroup, Label,
    Badge, Table, Spinner, Alert, Button
} from "reactstrap";
import { FiRefreshCw } from "react-icons/fi";
import { IoDocumentTextOutline } from "react-icons/io5";
import { MdBusiness, MdPerson } from "react-icons/md";
import { FaSistrix } from "react-icons/fa";
import { NumberWithCommas } from "../../share/NumberWithCommas";
import CreateTableComponent from '../../share/CreateTableComponent';



const SUPPORT_TYPE_MAP = {
    1: "ประกาศข้อ 4",
    2: "ประกาศข้อ 5",
    4: "ประกาศข้อ 6",
    5: "ประกาศข้อ 7",
    6: "ประกาศข้อ 8",
    3: "ประกาศข้อ 10 ",
    9: "ประกาศข้อ 10 ",
};

const Line = ({ label, value }) => (
    <div className="d-flex justify-content-between align-items-start py-1">
        <div className="text-muted">{label}</div>
        <div className="fw-semibold text-end ms-3" style={{ maxWidth: 360 }}>{value ?? "-"}</div>
    </div>
);

const SupportHeader = ({ data, onRefresh, loading }) => {
    const title = data?.supportName || "—";
    const typeId = data?.supportTypeId;
    const badgeText = SUPPORT_TYPE_MAP[typeId] || `ประเภท ${typeId ?? "-"}`;

    return (
        <Card className="shadow-sm border-0">
            <CardHeader
                className="border-0"
                style={{ background: "linear-gradient(90deg, rgba(14,165,233,0.12) 0%, rgba(99,102,241,0.12) 100%)" }}
            >
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <IoDocumentTextOutline size={22} className="text-primary" />
                        <h5 className="m-0">{title}</h5>
                        {/* {typeId ? <Badge color={typeToColor(typeId)} pill className="ms-1">{badgeText}</Badge> : null} */}
                    </div>

                    <Button color="light" disabled={loading} onClick={onRefresh}>
                        <FiRefreshCw className={loading ? "spin" : ""} />
                        <span className="ms-2">รีเฟรช</span>
                    </Button>
                </div>
            </CardHeader>

            <CardBody>
                <Row className="g-3">
                    <Col md="6">
                        <Card className="border-0 shadow-sm h-100">
                            <CardHeader className="bg-white border-0 pb-0">
                                <div className="d-flex align-items-center gap-2">
                                    <MdBusiness className="text-info" />
                                    <h6 className="m-0">ข้อมูลผู้ขอรับเงินช่วยเหลือหรืออุดหนุน</h6>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <Line label="ชื่อผู้ขอ" value={data?.supportName} />
                                <Line label="เลขทะเบียนนิติบุคคล" value={data?.customerNo} />
                                <Line label="เลขสถานประกอบการ" value={data?.companyNo} />
                                <Line label="ประเภทธุรกิจ" value={data?.businessType} />

                                {/* <Line label="ปีงบประมาณ" value={data?.budgetYear} /> */}
                                <Line label="จำนวนลูกจ้างทั้งหมด (คน)" value={data?.employeeAmount} />
                                <Line label="สั่งจ่ายในนาม" value={data?.paymentName} />
                                <Line label="เลขที่ใบอนุญาตการเป็นผู้ดำเนินการทดสอบ ตามมาตรา 24" value={data?.testLicense24No} />
                                {/* <Line label="ใบรับรอง 264" value={data?.certificate264} /> */}
                                {/* <Line label="ปีข้อมูล (year1)" value={data?.year1} /> */}
                                {/* <Line
                                    label="วันเริ่ม/สิ้นสุดใบรับรอง"
                                    value={`${data?.startDateCertificate ?? "-"} - ${data?.endDateCertificate ?? "-"}`}
                                /> */}
                            </CardBody>
                        </Card>
                    </Col>

                    <Col md="6">
                        <Card className="border-0 shadow-sm h-100">
                            <CardHeader className="bg-white border-0 pb-0">
                                <div className="d-flex align-items-center gap-2">
                                    <MdPerson className="text-success" />
                                    <h6 className="m-0">ผู้มีอำนาจ</h6>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <Line label="ชื่อ-สกุล" value={data?.authFullName} />
                                <Line label="เลขบัตรประชาชน" value={data?.authPersonalId} />
                                <Line
                                    label="ที่อยู่"
                                    value={[
                                        data?.authAddressNo ? `เลขที่ ${data?.authAddressNo}` : null,
                                        data?.authAddressMoo ? `หมู่ ${data?.authAddressMoo}` : null,
                                        data?.authAddressSoi ? `ซอย ${data?.authAddressSoi}` : null,
                                        data?.authAddressStreet ? `ถนน ${data?.authAddressStreet}` : null,
                                        data?.authAddressTumbonName ? `ต.${data?.authAddressTumbonName}` : null,
                                        data?.authAddressAmphurName ? `อ.${data?.authAddressAmphurName}` : null,
                                        data?.authAddressProvinceName ? `จ.${data?.authAddressProvinceName}` : null,
                                        data?.authAddressPostCode ? `${data?.authAddressPostCode}` : null,
                                    ].filter(Boolean).join(" ")}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

const SimpleTable = ({ columns, rows, keyField = "id" }) => (
    <div className="table-responsive">
        <Table hover bordered className="align-middle">
            <thead className="table-light">
                <tr>{columns.map((c) => <th key={c.key} className={c.thClassName}>{c.header}</th>)}</tr>
            </thead>
            <tbody>
                {rows?.length ? rows.map((r, idx) => (
                    <tr key={r[keyField] ?? `${idx}`}>
                        {columns.map((c) => (
                            <td key={c.key} className={c.tdClassName}>
                                {c.render ? c.render(r[c.key], r, idx) : (r[c.key] ?? "-")}
                            </td>
                        ))}
                    </tr>
                )) : (
                    <tr><td colSpan={columns.length} className="text-center text-muted py-4">ไม่มีข้อมูล</td></tr>
                )}
            </tbody>
        </Table>
    </div>
);

const DetailAnnounce4 = (data) => {
    // console.log("data", data)
    const [certificate24Person, setCertificate24Person] = useState([]);
    const [certificate24Data, setCertificate24Data] = useState([]);
    const [conditionData, setConditionData] = useState([]);
    const [calculateAmount, setCalculateAmount] = useState(0);

    const COLUMNSANNOUNCE4 = [
        {
            Header: "ลำดับ",
            accessor: (_row, i) => i + 1,
        },
        {
            Header: "วันที่ได้รับใบอนุญาต",
            accessor: "allowDate",
        },
        {
            Header: "สาขาอาชีพ",
            accessor: "lawOccupationName",
        },
        {
            Header: "สาขา",
            accessor: "testOccupationName",
        },
        {
            Header: "ระดับ",
            accessor: "personId",
        },
        {
            Header: "จำนวนผู้เข้ารับการทดสอบทั้งหมด",
            accessor: "amount",
        },
    ];
    useEffect(() => {
        checkCertificate24(data?.view?.testLicense24No);
        getConditionSupport();
    }, [data?.view?.testLicense24No])

    useEffect(() => {
        checkTotalPerson24(conditionData, data?.view?.testLicense24No);
    }, [conditionData, data?.view?.testLicense24No])
    // เอามาแสดงเฉยๆ
    const checkCertificate24 = async (certificate24) => {
        // console.log("certificate24", certificate24);
        try {
            let postData = new FormData()
            // console.log(certificate24)
            postData.append("certificate24No", certificate24)

            let res = await fetch(`api/SupportForm/checkDataAnnounceFour`, {
                method: "POST",
                body: postData
            })

            let resJson = await res.json();
            // console.log("resJson.status", resJson.status)
            setCertificate24Data(resJson.dataenum)

        } catch (e) {
            // console.log(e)
        }
    }
    const getConditionSupport = async () => {

        try {
            let res = await fetch(`api/SupportForm/getConditionSupport/${1}`)
            let resJson = await res.json();
            // console.log(resJson.dataenum);
            setConditionData(resJson.dataenum);

        } catch (e) {
            // console.log('err => ', e)
        }
    }

    //เอามาเพื่อ check ว่า จำนวนทั้งหมด และวันที่เกินมั้ย 
    const checkTotalPerson24 = async (conditionData, certificate24) => {
        try {
            let postData = new FormData()
            // console.log(certificate24)
            postData.append("certificate24No", certificate24)

            let res = await fetch(`api/SupportForm/checkPersonAnnounceFour`, {
                method: "POST",
                body: postData
            })

            let resJson = await res.json();
            // console.log("resJson", resJson.dataenum);
            // console.log("resJson.status", resJson.status)
            setCertificate24Person(resJson.dataenum)
            let conditionOne;
            let conditionTwo;
            // let conditionDate;
            conditionData.map((condition) => {
                conditionOne = condition.condition1;
                conditionTwo = condition.condition2;
            })
            // console.log("conditionOne", conditionOne);
            // console.log("conditionTwo", conditionTwo);
            let total = resJson.dataenum.map(({ total }) => total).join().toString();
            // console.log("before total", total);
            total = Number(total)
            // console.log("after total", total);


            if (total > 100) {
                let base = 100;
                let baseCalculate = total - base;
                // console.log("baseCalculate", baseCalculate);
                let moreExcess = baseCalculate * Number(conditionTwo);
                // console.log("moreExcess", moreExcess);
                let totalSummary = Number(conditionOne) + moreExcess;
                // console.log("totalSummary", totalSummary);
                setCalculateAmount(totalSummary)
            }

        } catch (e) {
            // console.log(e)
        }
    }
    return (
        <>
            {certificate24Person?.map((item => (
                <div className="mt-4">
                    <Label className="">
                        รวมจำนวนผู้ผ่านการทดสอบ จำนวน <span className="input-color">{item.total}</span> คน
                    </Label>
                </div>
            )))}

            <CreateTableComponent
                column={COLUMNSANNOUNCE4}
                dataInput={certificate24Data}
                showEntrie={false}
                showPage={false}
            />
        </>
    );
};

const DetailAnnounce6 = ({ item }) => {
    // console.log("item", item);
    return (
        <>
            <FormGroup>
                <Label className="mb-2 input-color" style={{ fontWeight: "bold" }}>
                    2. รายละเอียดการดำเนินการพัฒนาฝีมือแรงงาน
                </Label>

                <p className="type-register">ในปี พ.ศ. {item.receiveYear} (ปีที่ผ่านมา) มีลูกจ้างเฉลี่ยรวมทั้งสิ้น จำนวน <span className="mx-1">{item.avgMan}</span> คน</p>
                <p className="type-register">ได้ดำเนินการพัฒนาฝีมือแรงงานให้แก่ลูกจ้าง จำนวน  <span className="mx-1">{item.spkTrainMan} คน</span> คิดเป็นร้อยละ <span className="mx-1">{item.trainPercent}</span> ของลูกจ้างทั้งหมด (ไม่ซ้ำคน) ดังนี้ </p>
                <div className="px-1">
                    <ul>
                        <li>
                            <div className="d-flex justify-content-start">
                                <span className="type-register">จัดให้มีการฝึกอบรมฝีมือแรงงาน จำนวน</span>
                                <span className="type-register mx-1">{item.train ? item.train : "-"} คน</span>
                            </div>

                        </li>
                        <li>
                            <div className="d-flex justify-content-start">
                                <span className="type-register">ผ่านการทดสอบมาตรฐานฝีมือแรงงานแห่งชาติ จำนวน</span>
                                <span className="type-register mx-1">{item.test ? item.test : "-"} คน</span>
                            </div>
                        </li>
                        <li>
                            <div className="d-flex justify-content-start">
                                <span className="type-register">ผ่านการรับรองความรู้ความสามารถ จำนวน</span>
                                <span className="type-register mx-1">{item.oloc ? item.oloc : "-"} คน</span>
                            </div>

                        </li>
                    </ul>
                </div>

                {(() => {
                    // Helpers
                    const N = v => Number(v ?? 0);
                    const INT = v => Math.max(0, Math.floor(Number(v ?? 0)));
                    const money = v => NumberWithCommas(v ?? 0);
                    console.log("item", item);
                    // ใช้เรคอร์ดแรกของผลตรวจสิทธิ์
                    const a6 = (item && item[0]) || {};
                    console.log("a6", a6);
                    // ค่าพื้นฐาน
                    const avgMan = N(item?.avgMan);         // ลูกจ้างเฉลี่ย
                    console.log("avgMan", avgMan);

                    const avg70 = INT(item?.avg70);        // 70% (มีมาจาก backend)
                    const avg80 = Math.ceil(avgMan * 0.8);    // 80% ของลูกจ้างเฉลี่ย ปัดขึ้นเป็นจำนวนเต็ม
                    // console.log("avg80", avg80);
                    // console.log("avg70", avg70);
                    const trainedTotal = item.trainPercent > 100 ? avgMan : INT(item?.spkTrainMan);  // จำนวนผู้พัฒนาฝีมือ "ทั้งหมด"
                    console.log("trainedTotal", trainedTotal);

                    // ขั้นที่ 1: ช่วง 70–80%
                    // const step1Count = Math.max(0, avg80 - avg70);
                    const step1Count = item?.cntGt70Lt80;

                    const step1Rate = 200;
                    const step1Amount = item?.amt70;

                    // ขั้นที่ 2: เกิน 80–100% (ไม่เกิน 100% = avgMan)
                    const step2Count = trainedTotal - avg80;
                    const step2Rate = 300;
                    const step2Amount = item?.amt80;

                    // รวม
                    const totalAmount = step1Amount + step2Amount;
                    // setCalculateAmount(totalAmount);
                    return (
                        <FormGroup className="mt-4">
                            <Label className="mb-2 input-color" style={{ fontWeight: "bold" }}>
                                3. การคำนวณเงินช่วยเหลืออุดหนุน
                            </Label>

                            {/* ขั้นที่ 1 */}
                            <div className="px-2">
                                <p style={{ fontWeight: 600 }}>
                                    ขั้นที่ 1: ในส่วนที่เกินกว่าร้อยละ 70 แต่ไม่เกินร้อยละ 80 ของลูกจ้างเฉลี่ย
                                </p>
                                <ul className="type-register">
                                    <li>
                                        3.1 จำนวนลูกจ้างที่ดำเนินการพัฒนาฝีมือแรงงานร้อยละ 70 =
                                        <span className="mx-1">{avg70}</span> คน
                                    </li>
                                    <li>
                                        3.2 จำนวนลูกจ้างที่ดำเนินการพัฒนาฝีมือแรงงานร้อยละ 80 =
                                        <span className="mx-1">{avg80}</span> คน
                                    </li>
                                    <li>
                                        3.3 จำนวนลูกจ้างช่วง 70–80% =
                                        <span className="mx-1">{step1Count}</span> คน
                                    </li>
                                    <li>
                                        3.4 อัตราเงินช่วยเหลือฯ ส่วน 70–80 =
                                        <span className="mx-1">{step1Rate}</span> บาท/คน
                                    </li>
                                    <li>
                                        3.5 เงินช่วยเหลือขั้นที่ 1 (ข้อ 3.3 × {step1Rate}) =
                                        <span className="mx-1">{money(step1Amount)}</span> บาท
                                    </li>
                                </ul>
                            </div>

                            {/* ขั้นที่ 2 */}
                            <div className="px-2 mt-3">
                                <p style={{ fontWeight: 600 }}>
                                    ขั้นที่ 2: ในส่วนที่เกินกว่าร้อยละ 80 แต่ไม่เกินร้อยละ 100 ของลูกจ้างเฉลี่ย
                                </p>
                                <ul className="type-register">
                                    <li>
                                        3.6 จำนวนลูกจ้างที่พัฒนาฝีมือทั้งหมด =
                                        <span className="mx-1">{trainedTotal}</span> คน
                                    </li>
                                    <li>
                                        3.7 ร้อยละ 80 ของลูกจ้างเฉลี่ย (ตามข้อ 3.2) =
                                        <span className="mx-1">{avg80}</span> คน
                                    </li>
                                    <li>
                                        3.8 จำนวนที่เกิน 80–100% =
                                        <span className="mx-1">{step2Count < 0 ? 0 : step2Count}</span> คน
                                    </li>
                                    <li>
                                        3.9 อัตราเงินช่วยเหลือฯ ส่วน 80–100 =
                                        <span className="mx-1">{step2Rate}</span> บาท/คน
                                    </li>
                                    <li>
                                        3.10 เงินช่วยเหลือขั้นที่ 2 (ข้อ 3.8 × {step2Rate}) =
                                        <span className="mx-1">{money(step2Amount)}</span> บาท
                                    </li>
                                </ul>
                            </div>

                            {/* รวม */}
                            <div className="px-2 mt-3">
                                <p className="type-register" style={{ fontWeight: 600 }}>
                                    ทั้งหมด คิดเป็นจำนวนเงินรวม (ข้อ 3.5 + ข้อ 3.10) =
                                    <span className="mx-1">{money(totalAmount)}</span> บาท
                                </p>
                            </div>
                        </FormGroup>
                    );
                })()}
            </FormGroup>
        </>
    )
}

/** ประกาศ 5: ใช้ field ใน data.detailView (camelCase อยู่แล้วจาก backend) */
const DetailAnnounce5 = ({ view }) => {
    const items = [
        { label: "ปี พ.ศ.", value: view?.year1 },
        { label: "วันที่นำส่งเงิน", value: view?.receiptDate },
        { label: "จำนวนลูกจ้างเฉลี่ยรวม (คน)", value: view?.avgMan },
        { label: "ร้อยละ", value: view?.perc },
        { label: "จำนวน", value: view?.lawTrainMan },
        { label: "ได้ดำเนินการพัฒนาฝีมือแรงงานให้แก่ลูกจ้างครบตามสัดส่วนที่กฎหมายกำหนด", value: view?.spkTrainMan },
        { label: "จัดให้มีการฝึกอบรมฝีมือแรงงาน", value: view?.train },
        { label: "จัดให้มีการทดสอบมาตรฐานฝีมือแรงงาน", value: view?.test },
        { label: "ผ่านการรับรองความรู้ความสามารถ", value: view?.oloc },

        { label: "จำนวนเงิน", value: NumberWithCommas(view?.amount) },

    ];

    return (
        <Card className="shadow-sm border-0">
            <CardHeader className="bg-white border-0"><h6 className="m-0">รายละเอียด: ประกาศ 5 (ใหม่)</h6></CardHeader>
            <CardBody>
                <Row>
                    {items.map((it, i) => (
                        <Col md="6" key={i} className="mb-2">
                            <div className="small text-muted">{it.label}</div>
                            <div className="fw-semibold">{it.value ?? "-"}</div>
                        </Col>
                    ))}
                </Row>
            </CardBody>
        </Card>
    );
};

/** ประกาศ 7 (supportTypeId = 5) */
const DetailAnnounce7 = ({ list }) => {
    const columns = [
        { key: "cer2Num", header: "เลข รมฐ.2" },
        { key: "careerName", header: "สาขาอาชีพ" },
        { key: "occupationName", header: "สาขา" },
        { key: "occupationLevel", header: "ระดับ" },
        { key: "cer2ApproveDate", header: "วันที่ออก" },
        { key: "personTotal", header: "จำนวนทดสอบ (คน)" },
        { key: "employeeTotal", header: "ลูกจ้างในตำแหน่งงาน (คน)" },
    ];
    return (
        <Card className="shadow-sm border-0">
            <CardHeader className="bg-white border-0"><h6 className="m-0">รายละเอียด: ประกาศ 7 (ใหม่)</h6></CardHeader>
            <CardBody><SimpleTable columns={columns} rows={list} /></CardBody>
        </Card>
    );
};

/** ประกาศ 8 (supportTypeId = 6) */
const DetailAnnounce8 = ({ list }) => {
    const columns = [
        { key: "fullName", header: "ชื่อ-สกุล" },
        { key: "lawOccupationName", header: "สาขาอาชีพ" },
        { key: "testOccupationName", header: "สาขา " },
        { key: "testOccupationLevel", header: "ระดับ" },
        { key: "payRate", header: "อัตราค่าจ้าง" },
        { key: "payRateAmount", header: "จำนวนเงิน" },
        { key: "payRateStartDate", header: "เริ่มจ่ายค่าจ้าง" },
        { key: "payRateEndDate", header: "งวดสุดท้าย" },
        { key: "certificateNo", header: "เลขที่หนังสือรับรอง" },
    ];
    return (
        <Card className="shadow-sm border-0">
            <CardHeader className="bg-white border-0"><h6 className="m-0">รายละเอียด: ประกาศ 8 (ใหม่)</h6></CardHeader>
            <CardBody><SimpleTable columns={columns} rows={list} /></CardBody>
        </Card>
    );
};

const openInNewTab = (url) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
}

function onClickUrl(url) {
    return () => openInNewTab(url)
}
/** ประกาศ 10 (supportTypeId = 3 หรือ 9) */
const DetailAnnounce10 = ({ list, variant }) => {
    const columns = [
        { key: "lawOccupationName", header: "สาขาอาชีพ " },
        { key: "testOccupationName", header: "สาขา" },
        { key: "personTotal", header: "จำนวนคนทั้งหมด" },
        { key: "receiptNo", header: "เลขที่ใบเสร็จ" },
        { key: "receipt", header: "ใบเสร็จเล่มที่" },
        { key: "receiptDate", header: "วันที่ใบเสร็จ" },
        { key: "certificateNo", header: "เลขที่หนังสือรับรอง" },
        { key: "amountTotal", header: "จำนวนเงินรวม" },
    ];
    return (
        <Card className="shadow-sm border-0">
            <CardHeader className="bg-white border-0"><h6 className="m-0">รายละเอียด: ประกาศ 10 ({variant})</h6></CardHeader>
            <CardBody><SimpleTable columns={columns} rows={list} /></CardBody>
        </Card>
    );
};

const DetailPlaceholder = ({ label = "รายละเอียด" }) => (
    <Alert color="light" className="border-0 shadow-sm">{label} ยังไม่พร้อมแสดงผล หรือไม่มีข้อมูลในระบบ</Alert>
);

export function DetailSupport({ supportId }) {
    const [data, setData] = useState(null);

    // console.log("data", data);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const fetchData = async () => {
        try {
            setLoading(true);
            setErr("");
            const res = await fetch(`/api/SupportForm/detailSupport/${supportId}`, {
                headers: { "Accept": "application/json" },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            // รองรับทั้ง { data: {...} } / { dataenum: {...} } / {...}
            const payload = json?.data ?? json?.dataenum ?? json;
            setData(payload);
        } catch (e) {
            console.error(e);
            setErr("ไม่สามารถโหลดข้อมูลได้");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (supportId) fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supportId]);

    const typeId = data?.supportTypeId;
    const typeLabel = useMemo(() => SUPPORT_TYPE_MAP[typeId] || "", [typeId]);

    return (
        <Container className="py-3">
            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 180 }}>
                    <Spinner /> <span className="ms-2">กำลังโหลดข้อมูล…</span>
                </div>
            ) : err ? (
                <Alert color="danger" className="shadow-sm">
                    {err}
                    <div className="mt-2"><Button color="light" size="sm" onClick={fetchData}>ลองใหม่</Button></div>
                </Alert>
            ) : (
                <>
                    <SupportHeader data={data} onRefresh={fetchData} loading={loading} />
                    <div className="my-3" />
                    {typeId === 4 && <DetailAnnounce5 view={data?.detailView} />}
                    {typeId === 6 && <DetailAnnounce7 list={data?.detail} />}
                    {typeId === 7 && <DetailAnnounce8 list={data?.detail} />}
                    {(typeId === 3 || typeId === 9) && (
                        <DetailAnnounce10
                            list={data?.detail}
                            variant={typeId === 3 ? "ศูนย์ทดสอบ" : "ศูนย์ประเมิน"}
                        />
                    )}
                    {typeId === 1 && <DetailAnnounce4 view={data} />}
                    {typeId === 5 && <DetailAnnounce6 item={data?.detailView} />}



                    <div className="border border-1 w-100">
                        {data?.fIdCardPathView != null || data?.fOtherPathView != null || data.fCertificatePathView != null || data.fSsoPathView != null ? (
                            <>
                                <p className="text-center text-white" style={{ lineHeight: "3rem", backgroundColor: "#0A66C2" }}>ข้อมูลเพิ่มเติมและเอกสารประกอบการยื่น</p>
                                <div className="d-flex flex-column">
                                    {data?.fIdCardPathView != null ? (
                                        <FormGroup className='d-flex justify-content-between'>
                                            <Label className="px-3">บัตรประจำตัวประชาชน</Label>
                                            <FaSistrix className="mx-3 style-icon" onClick={onClickUrl(`${data.fIdCardPathView}`)} />
                                        </FormGroup>
                                    ) : null}
                                    {data?.fOtherPathView != null ? (
                                        <FormGroup className='d-flex justify-content-between'>
                                            <Label className="px-3">เอกสารอื่นๆ</Label>
                                            <FaSistrix className="mx-3 style-icon" onClick={onClickUrl(`${data.fOtherPathView}`)} />
                                        </FormGroup>
                                    ) : null}
                                    {data?.fCertificatePathView != null ? (
                                        <FormGroup className='d-flex justify-content-between'>
                                            <Label className="px-3">หนังสือรับรองมาตรฐานฝีมือแรงงาน</Label>
                                            <FaSistrix className="mx-3 style-icon" onClick={onClickUrl(`${data.fCertificatePathView}`)} />
                                        </FormGroup>
                                    ) : null}

                                    {data?.fSsoPathView != null ? (
                                        <FormGroup className='d-flex justify-content-between'>
                                            <Label className="px-3">หนังสือรายการส่งเงินสบทบทุน</Label>
                                            <FaSistrix className="mx-3 style-icon" onClick={onClickUrl(`${data.fSsoPathView}`)} />
                                        </FormGroup>
                                    ) : null}
                                    {data?.fEmployeeCertTotal != null ? (
                                        <FormGroup className='d-flex justify-content-between'>
                                            <Label className="px-3">เอกสารที่แสดงจำนวนลูกจ้างทั้งหมดในตำแหน่งงานที่ดำเนินการทดสอบมาตรฐานฝีมือแรงงาน</Label>
                                            <FaSistrix className="mx-3 style-icon" onClick={onClickUrl(`${data.fEmployeeCertTotal}`)} />
                                        </FormGroup>
                                    ) : null}
                                    {data?.fCertificateTest != null ? (
                                        <FormGroup className='d-flex justify-content-between'>
                                            <Label className="px-3">หนังสือรับรองว่าเป็นผู้ผ่านการทดสอบมาตรฐานฝีมือแรงงาน</Label>
                                            <FaSistrix className="mx-3 style-icon" onClick={onClickUrl(`${data.fCertificateTest}`)} />
                                        </FormGroup>
                                    ) : null}
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-center text-white" style={{ lineHeight: "3rem", backgroundColor: "#0A66C2" }}>ข้อมูลเพิ่มเติมและเอกสารประกอบการยื่น</p>
                                <div className="d-flex flex-column align-items-center justify-content-center p-2">
                                    ไม่มีเอกสารประกอบการยื่นเพิ่มเติม
                                </div>
                            </>
                        )}



                    </div>






                </>
            )}
        </Container>
    );
}

// เพิ่ม CSS สำหรับไอคอนโหลด
// .spin { animation: spin 1.1s linear infinite; }
// @keyframes spin { to { transform: rotate(360deg); } }
